import { useState } from "react";

import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { useMutation } from "@tanstack/react-query";

import { asQuery } from "simplydo/core/query";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { H2 } from "simplydo/react/ui/header";
import { Message } from "simplydo/react/ui/message";

import api from "api";
import { useCurrentUser } from "utils/currentUser";

const EXTENSION_REDIRECT = /^([a-p]{32})\.chromiumapp\.org$/;

const allowedExtensionIds = String(
  import.meta.env.VITE_CHROME_EXTENSION_IDS ?? "",
)
  .split(",")
  .map((id) => id.trim())
  .filter(Boolean);

// Only our own extensions may receive a login token, so another extension can't use this page to sign in as the user
const extensionRedirect = (redirectUri: string | null) => {
  if (!redirectUri) {
    return null;
  }
  try {
    const url = new URL(redirectUri);
    const extensionId = url.hostname.match(EXTENSION_REDIRECT)?.[1];
    if (
      url.protocol !== "https:" ||
      !extensionId ||
      !allowedExtensionIds.includes(extensionId)
    ) {
      return null;
    }
    return url;
  } catch {
    return null;
  }
};

const ConnectChromeExtension = () => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const [params] = useSearchParams();
  const [cancelled, setCancelled] = useState(false);
  const redirect = extensionRedirect(params.get("redirect_uri"));
  const state = params.get("state") ?? "";

  const returnToExtension = (result: Record<string, string>) => {
    if (!redirect) {
      return;
    }
    redirect.hash = new URLSearchParams({ ...result, state }).toString();
    window.location.replace(redirect.toString());
  };

  const connect = useMutation({
    mutationFn: async () => {
      const { exchangeToken } = await asQuery(
        api.auth.generateExchangeToken,
        [],
      )();
      if (typeof exchangeToken !== "string") {
        throw new Error("No exchange token");
      }
      return exchangeToken;
    },
    onSuccess: (exchangeToken) => returnToExtension({ exchangeToken }),
  });

  return (
    <Card style={{ maxWidth: 480, margin: "40px auto" }}>
      <CardContent>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <H2 style={{ margin: 0 }}>{t("connect.chrome.title")}</H2>
          {!redirect ? (
            <Message color="red">{t("connect.chrome.invalid")}</Message>
          ) : cancelled ? (
            <p>{t("connect.chrome.cancelled")}</p>
          ) : (
            <>
              <p>
                {t("connect.chrome.info", { name: user?.profile?.fullName })}
              </p>
              {connect.isError ? (
                <Message color="red">{t("connect.chrome.error")}</Message>
              ) : null}
              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  color="secondary"
                  loading={connect.isPending}
                  onClick={() => connect.mutate()}
                >
                  {t("connect.chrome.connect")}
                </Button>
                <Button
                  variant="outline"
                  disabled={connect.isPending}
                  onClick={() => {
                    setCancelled(true);
                    returnToExtension({ error: "access_denied" });
                  }}
                >
                  {t("generic.cancel")}
                </Button>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ConnectChromeExtension;
